import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, ArrowRight, Check, Plus, Trash2, MessageCircle, Copy, CheckCircle } from 'lucide-react';
import { ApcMark } from '../components/ApcMark';
import { api, formatApiError, fmtZAR } from '../lib/api';
import { WHATSAPP_URL } from '../lib/constants';

const emptyLearner = () => ({
  full_name: '', curriculum: 'CAPS', grade: 10, subjects: [], google_email: '', returning: false,
});

const stepTitles = [
  'Parent / guardian',
  'Learners & subjects',
  'Review',
];

const RegisterPage = () => {
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState({ academic_year: 2027, offerings: [] });
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const [parent, setParent] = useState({ parent_name: '', whatsapp: '', email: '', consent_privacy: false, consent_guardian: false });
  const [learners, setLearners] = useState([emptyLearner()]);

  useEffect(() => {
    api.get('/catalog').then((r) => setCatalog(r.data)).catch(() => {});
  }, []);

  const findOffering = (curr, grade) => catalog.offerings.find((o) => o.curriculum === curr && o.grades.includes(Number(grade)));

  const totalCents = learners.reduce((sum, l) => {
    const o = findOffering(l.curriculum, l.grade);
    return sum + (o ? o.price_cents * l.subjects.length : 0);
  }, 0);

  const canNext = () => {
    if (step === 0) {
      return parent.parent_name.trim().length >= 2 && parent.whatsapp.trim().length >= 6 && parent.consent_privacy && parent.consent_guardian;
    }
    if (step === 1) {
      return learners.length > 0 && learners.every((l) => {
        const o = findOffering(l.curriculum, l.grade);
        return l.full_name.trim().length >= 2 && /@/.test(l.google_email) && l.subjects.length > 0 && !!o && l.subjects.every((s) => o.subjects.includes(s));
      });
    }
    return true;
  };

  const submit = async () => {
    setError(''); setSubmitting(true);
    try {
      const payload = { ...parent, email: parent.email || null, learners };
      const { data } = await api.post('/register', payload);
      setResult(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const copyRef = () => {
    if (!result) return;
    navigator.clipboard.writeText(result.reference);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (result) {
    return (
      <div className="min-h-screen bg-[#FAF9F6] py-10 md:py-16" data-testid="registration-success-page">
        <div className="container-apc max-w-2xl">
          <Link to="/" className="inline-flex items-center gap-2 mb-6 text-sm text-[#4B5563] hover:text-[#1F6F4A]"><ArrowLeft size={14}/> Back home</Link>
          <div className="bg-white rounded-3xl border border-black/5 p-8 md:p-12 shadow-sm">
            <div className="w-14 h-14 rounded-full bg-[#1F6F4A]/10 flex items-center justify-center mb-5">
              <CheckCircle size={28} className="text-[#1F6F4A]"/>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#1B2A41] mb-3" style={{ fontFamily: 'Outfit' }}>You're registered.</h1>
            <p className="text-[#4B5563] mb-7">Thank you — we've logged your registration for the APC 2027 intake.</p>

            <div className="bg-[#F3F5F2] rounded-2xl p-5 mb-6">
              <p className="text-xs font-mono tracking-wider text-[#4B5563]">REGISTRATION REFERENCE</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-2xl font-black text-[#1B2A41]" data-testid="registration-reference">{result.reference}</p>
                <button onClick={copyRef} className="btn-pill btn-outline !py-2 !text-xs" data-testid="copy-reference">
                  {copied ? 'Copied' : <>Copy <Copy size={12}/></>}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between py-4 border-y border-black/5 mb-6">
              <p className="font-semibold text-[#1B2A41]">Monthly total</p>
              <p className="text-2xl font-black text-[#1F6F4A]" data-testid="registration-total">{fmtZAR(result.total_cents)}</p>
            </div>

            <p className="text-sm font-semibold text-[#1B2A41] mb-2">Payment instructions</p>
            <pre className="whitespace-pre-wrap text-sm text-[#4B5563] bg-[#FAF9F6] p-4 rounded-xl border border-black/5 font-sans" data-testid="payment-instructions">{result.payment_instructions || 'Please contact APC on WhatsApp for banking details.'}</pre>

            <p className="text-xs text-[#4B5563] mt-4">Your registration is marked <strong>Pending Payment</strong>. Google Classroom access is granted once your first monthly payment is received and verified by APC.</p>

            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="btn-pill btn-primary flex-1 justify-center" data-testid="success-whatsapp">
                <MessageCircle size={16}/> Message APC on WhatsApp
              </a>
              <Link to="/" className="btn-pill btn-outline flex-1 justify-center">Done</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F6] py-10 md:py-16" data-testid="registration-page">
      <div className="container-apc max-w-3xl">
        <Link to="/" className="inline-flex items-center gap-2 mb-6 text-sm text-[#4B5563] hover:text-[#1F6F4A]"><ArrowLeft size={14}/> Back home</Link>

        <div className="flex items-center gap-3 mb-8">
          <ApcMark size={30}/>
          <div>
            <p className="text-xs font-mono tracking-wider text-[#D4A017]">APC {catalog.academic_year} REGISTRATION</p>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1B2A41]" style={{ fontFamily: 'Outfit' }}>Register for {catalog.academic_year}</h1>
          </div>
        </div>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-8" data-testid="stepper">
          {stepTitles.map((t, i) => (
            <div key={t} className="flex-1 flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${i <= step ? 'bg-[#1F6F4A] text-white' : 'bg-black/5 text-[#4B5563]'}`}>{i < step ? <Check size={14}/> : i + 1}</div>
              <span className={`text-xs md:text-sm font-medium hidden sm:block ${i === step ? 'text-[#1B2A41]' : 'text-[#4B5563]'}`}>{t}</span>
              {i < stepTitles.length - 1 && <div className={`flex-1 h-px ${i < step ? 'bg-[#1F6F4A]' : 'bg-black/10'}`}/>}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-3xl border border-black/5 p-6 md:p-10 shadow-sm">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="s0" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <h2 className="text-xl font-bold text-[#1B2A41] mb-6" style={{ fontFamily: 'Outfit' }}>About you</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field label="Full name" required>
                    <input className="apc-input" value={parent.parent_name} onChange={(e) => setParent({ ...parent, parent_name: e.target.value })} data-testid="parent-name" />
                  </Field>
                  <Field label="WhatsApp number" required>
                    <input className="apc-input" value={parent.whatsapp} onChange={(e) => setParent({ ...parent, whatsapp: e.target.value })} placeholder="e.g. 064 063 9721" data-testid="parent-whatsapp"/>
                  </Field>
                  <Field label="Email (optional)" className="md:col-span-2">
                    <input type="email" className="apc-input" value={parent.email} onChange={(e) => setParent({ ...parent, email: e.target.value })} data-testid="parent-email"/>
                  </Field>
                </div>
                <div className="mt-6 space-y-3">
                  <label className="flex gap-3 items-start cursor-pointer">
                    <input type="checkbox" className="mt-1" checked={parent.consent_guardian} onChange={(e) => setParent({ ...parent, consent_guardian: e.target.checked })} data-testid="consent-guardian"/>
                    <span className="text-sm text-[#4B5563]">I confirm I am the parent or legal guardian of the learner(s) being registered.</span>
                  </label>
                  <label className="flex gap-3 items-start cursor-pointer">
                    <input type="checkbox" className="mt-1" checked={parent.consent_privacy} onChange={(e) => setParent({ ...parent, consent_privacy: e.target.checked })} data-testid="consent-privacy"/>
                    <span className="text-sm text-[#4B5563]">I accept APC's privacy notice and consent to the processing of my family's personal information for the purposes of academic enrolment and administration.</span>
                  </label>
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="s1" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <h2 className="text-xl font-bold text-[#1B2A41] mb-6" style={{ fontFamily: 'Outfit' }}>Learners & subjects</h2>
                <div className="space-y-4">
                  {learners.map((l, idx) => (
                    <LearnerCard
                      key={idx}
                      index={idx}
                      learner={l}
                      catalog={catalog}
                      onChange={(next) => setLearners(learners.map((x, i) => i === idx ? next : x))}
                      onRemove={() => setLearners(learners.filter((_, i) => i !== idx))}
                      canRemove={learners.length > 1}
                    />
                  ))}
                </div>
                <button
                  onClick={() => setLearners([...learners, emptyLearner()])}
                  className="btn-pill btn-outline mt-4"
                  data-testid="add-learner"
                >
                  <Plus size={14}/> Add another learner
                </button>
                <div className="mt-6 bg-[#F3F5F2] rounded-2xl p-4 flex items-center justify-between">
                  <p className="text-sm text-[#4B5563]">Live monthly total</p>
                  <p className="text-2xl font-black text-[#1F6F4A]" data-testid="live-total">{fmtZAR(totalCents)}</p>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="s2" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                <h2 className="text-xl font-bold text-[#1B2A41] mb-6" style={{ fontFamily: 'Outfit' }}>Review & confirm</h2>
                <div className="bg-[#FAF9F6] rounded-2xl p-5 mb-5 text-sm">
                  <p className="font-semibold text-[#1B2A41]">{parent.parent_name}</p>
                  <p className="text-[#4B5563]">WhatsApp: {parent.whatsapp}{parent.email && ` · ${parent.email}`}</p>
                </div>
                <div className="space-y-4">
                  {learners.map((l, i) => {
                    const o = findOffering(l.curriculum, l.grade);
                    return (
                      <div key={i} className="border border-black/10 rounded-2xl p-5" data-testid={`review-learner-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-semibold text-[#1B2A41]">{l.full_name}</p>
                          <p className="text-xs font-mono tracking-wider text-[#D4A017]">{l.curriculum} · GRADE {l.grade}</p>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-3">
                          {l.subjects.map((s) => (
                            <span key={s} className="text-xs px-2.5 py-1 bg-white border border-black/5 rounded-full">{s}</span>
                          ))}
                        </div>
                        <div className="text-sm text-[#4B5563] flex justify-between">
                          <span>{l.subjects.length} subject{l.subjects.length > 1 ? 's' : ''} × {o ? fmtZAR(o.price_cents) : 'R0'}</span>
                          <span className="font-semibold text-[#1B2A41]">{o ? fmtZAR(o.price_cents * l.subjects.length) : fmtZAR(0)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-6 bg-[#1F6F4A] text-white rounded-2xl p-5 flex items-center justify-between">
                  <p>Monthly total</p>
                  <p className="text-2xl font-black" data-testid="review-total">{fmtZAR(totalCents)}</p>
                </div>
                {error && <p className="mt-4 text-sm text-red-600" data-testid="submit-error">{error}</p>}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex items-center justify-between mt-8">
            <button
              onClick={() => setStep(Math.max(0, step - 1))}
              disabled={step === 0}
              className="btn-pill btn-outline disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="step-back"
            >
              <ArrowLeft size={16}/> Back
            </button>
            {step < 2 ? (
              <button
                onClick={() => canNext() && setStep(step + 1)}
                disabled={!canNext()}
                className="btn-pill btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="step-next"
              >
                Continue <ArrowRight size={16}/>
              </button>
            ) : (
              <button
                onClick={submit}
                disabled={submitting}
                className="btn-pill btn-primary disabled:opacity-40"
                data-testid="submit-registration"
              >
                {submitting ? 'Submitting…' : 'Submit registration'} <ArrowRight size={16}/>
              </button>
            )}
          </div>
        </div>
      </div>
      <style>{`.apc-input { width:100%; padding:0.7rem 0.9rem; border-radius:12px; border:1px solid rgba(27,42,65,0.15); background:#fff; font-size:0.95rem; color:#1B2A41; }
      .apc-input:focus { outline:none; border-color:#1F6F4A; box-shadow:0 0 0 4px rgba(31,111,74,0.08); }`}</style>
    </div>
  );
};

const Field = ({ label, required, children, className = '' }) => (
  <label className={`block ${className}`}>
    <span className="text-xs font-semibold text-[#4B5563] uppercase tracking-wider block mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </span>
    {children}
  </label>
);

const LearnerCard = ({ index, learner, catalog, onChange, onRemove, canRemove }) => {
  const offering = catalog.offerings.find((o) => o.curriculum === learner.curriculum && o.grades.includes(Number(learner.grade)));

  const toggleSubject = (subject) => {
    const next = learner.subjects.includes(subject)
      ? learner.subjects.filter((s) => s !== subject)
      : [...learner.subjects, subject];
    onChange({ ...learner, subjects: next });
  };

  // Available grades per curriculum
  const grades = new Set();
  catalog.offerings.filter((o) => o.curriculum === learner.curriculum).forEach((o) => o.grades.forEach((g) => grades.add(g)));
  const gradeList = Array.from(grades).sort((a, b) => a - b);

  return (
    <div className="border border-black/10 rounded-2xl p-5 md:p-6" data-testid={`learner-card-${index}`}>
      <div className="flex items-center justify-between mb-4">
        <p className="font-semibold text-[#1B2A41]">Learner {index + 1}</p>
        {canRemove && (
          <button onClick={onRemove} className="text-sm text-red-500 hover:underline flex items-center gap-1" data-testid={`remove-learner-${index}`}><Trash2 size={14}/> Remove</button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Learner's full name" required>
          <input className="apc-input" value={learner.full_name} onChange={(e) => onChange({ ...learner, full_name: e.target.value })} data-testid={`learner-name-${index}`}/>
        </Field>
        <Field label="Google account email (for Classroom)" required>
          <input className="apc-input" type="email" value={learner.google_email} onChange={(e) => onChange({ ...learner, google_email: e.target.value })} data-testid={`learner-email-${index}`}/>
        </Field>
        <Field label="Curriculum" required>
          <div className="inline-flex p-1 bg-[#F3F5F2] rounded-full gap-1 w-full">
            {['CAPS', 'IEB'].map((c) => (
              <button
                key={c}
                onClick={() => onChange({ ...learner, curriculum: c, grade: c === 'IEB' && learner.grade < 8 ? 8 : learner.grade, subjects: [] })}
                className={`flex-1 py-2 rounded-full text-sm font-semibold ${learner.curriculum === c ? 'bg-[#1F6F4A] text-white' : 'text-[#1B2A41]'}`}
                data-testid={`learner-${index}-curriculum-${c}`}
              >{c}</button>
            ))}
          </div>
        </Field>
        <Field label="Grade" required>
          <select
            value={learner.grade}
            onChange={(e) => onChange({ ...learner, grade: Number(e.target.value), subjects: [] })}
            className="apc-input"
            data-testid={`learner-grade-${index}`}
          >
            {gradeList.map((g) => <option key={g} value={g}>Grade {g}</option>)}
          </select>
        </Field>
        <Field label="Returning learner?" className="md:col-span-2">
          <label className="flex items-center gap-2 cursor-pointer text-sm text-[#4B5563]">
            <input type="checkbox" checked={learner.returning} onChange={(e) => onChange({ ...learner, returning: e.target.checked })} data-testid={`learner-returning-${index}`}/>
            Yes, we have been part of APC before
          </label>
        </Field>
      </div>

      <div className="mt-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#4B5563] mb-2">Subjects</p>
        {!offering ? (
          <p className="text-sm text-red-600" data-testid={`offering-missing-${index}`}>This curriculum is not offered for the selected grade.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid={`subjects-list-${index}`}>
            {offering.subjects.map((s) => {
              const on = learner.subjects.includes(s);
              return (
                <button
                  key={s}
                  onClick={() => toggleSubject(s)}
                  className={`text-sm px-3 py-1.5 rounded-full border transition-all ${on ? 'bg-[#1F6F4A] text-white border-[#1F6F4A]' : 'bg-white text-[#1B2A41] border-black/10 hover:border-[#1F6F4A]'}`}
                  data-testid={`learner-${index}-subject-${s.replace(/[^A-Za-z0-9]/g, '')}`}
                >
                  {on && <Check size={12} className="inline mr-1"/>}{s}
                </button>
              );
            })}
          </div>
        )}
        {offering && (
          <p className="text-xs text-[#4B5563] mt-2">{fmtZAR(offering.price_cents)} per subject / month · {learner.subjects.length} selected</p>
        )}
      </div>
    </div>
  );
};

export default RegisterPage;
